import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Lock, SearchX } from "lucide-react";
import { ApiError, api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { cn } from "@/lib/utils";

type Tone = "primary" | "danger" | "neutral";
const TONE: Record<Tone, string> = { primary: "bg-accent text-primary", danger: "bg-danger-soft text-danger", neutral: "bg-muted text-foreground" };

/** The shared frame for an empty, error or no-access message: what happened, and one clear next step */
export function StateCard({
  icon: Icon,
  tone = "primary",
  title,
  body,
  actions,
  note,
  compact,
  className,
  big,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  tone?: Tone;
  title: string;
  body?: React.ReactNode;
  actions?: React.ReactNode;
  note?: React.ReactNode;
  compact?: boolean;
  className?: string;
  /** A large figure instead of an icon (for 404) */
  big?: string;
}) {
  return (
    <div role="status" className={cn("flex flex-col items-center gap-2.5 text-center", compact ? "px-6 py-10" : "px-6 py-16", className)}>
      {big ? (
        <span className="font-mono text-[40px] font-semibold tracking-tight text-muted-foreground/60">{big}</span>
      ) : (
        Icon && (
          <span className={cn("flex size-12 items-center justify-center rounded-xl", TONE[tone])}>
            <Icon className="size-[22px]" />
          </span>
        )
      )}
      <h2 className="text-[15px] font-semibold text-foreground">{title}</h2>
      {body && <p className="max-w-[320px] text-[13px] text-muted-foreground">{body}</p>}
      {actions && <div className="mt-1 flex flex-wrap items-center justify-center gap-2">{actions}</div>}
      {note && <p className="font-mono text-[11px] text-muted-foreground/80">{note}</p>}
    </div>
  );
}

export const stateButton = {
  primary: "inline-flex h-8 items-center rounded-[7px] bg-primary px-3 text-[13px] font-semibold text-primary-foreground hover:bg-primary/90",
  dark: "inline-flex h-8 items-center rounded-[7px] bg-sidebar px-3.5 text-[13px] font-semibold text-white hover:bg-sidebar/90",
  outline: "inline-flex h-8 items-center rounded-[7px] border border-border bg-card px-3 text-[13px] font-medium text-foreground hover:bg-muted",
};

/**
 * An empty list. With filters or a search on it says nothing matches and offers to clear them; with none on
 * it is the first-use message with the way to add the first one.
 */
export function ListEmpty({
  noun,
  icon,
  filtered,
  onClear,
  createLabel,
  onCreate,
  firstUse,
  compact = true,
  error,
  onRetry,
}: {
  /** Plural, lower case: "leads" */
  noun: string;
  icon: React.ComponentType<{ className?: string }>;
  filtered: boolean;
  onClear?: () => void;
  createLabel?: string;
  onCreate?: () => void;
  /** What to tell someone who has none yet */
  firstUse?: string;
  compact?: boolean;
  /** The list could not be loaded: say so, with Try again, instead of calling it empty */
  error?: unknown;
  onRetry?: () => void;
}) {
  if (error) return <ErrorState compact={compact} title={`Couldn't load your ${noun}`} onRetry={onRetry} error={error} />;
  if (filtered) {
    return (
      <StateCard
        compact={compact}
        icon={SearchX}
        tone="neutral"
        title={`No ${noun} match`}
        body="Nothing fits the search or filters you have on right now."
        actions={
          <>
            {onClear && (
              <button type="button" onClick={onClear} className={stateButton.dark}>
                Clear filters
              </button>
            )}
            {onCreate && createLabel && (
              <button type="button" onClick={onCreate} className={stateButton.outline}>
                {createLabel}
              </button>
            )}
          </>
        }
      />
    );
  }
  return (
    <StateCard
      compact={compact}
      icon={icon}
      title={`No ${noun} yet`}
      body={firstUse}
      actions={
        onCreate && createLabel ? (
          <button type="button" onClick={onCreate} className={stateButton.primary}>
            {createLabel}
          </button>
        ) : undefined
      }
    />
  );
}

export function ErrorState({ title, body, onRetry, compact, error }: { title: string; body?: string; onRetry?: () => void; compact?: boolean; error?: unknown }) {
  const ref = error instanceof ApiError && error.requestId ? error.requestId.slice(0, 8) : null;
  return (
    <StateCard
      compact={compact}
      icon={AlertTriangle}
      tone="danger"
      title={title}
      body={body || "Nothing you did. Your saved work is safe. Try again in a moment."}
      actions={
        onRetry && (
          <button type="button" onClick={onRetry} className={stateButton.dark}>
            Try again
          </button>
        )
      }
      note={ref ? `Ref ${ref} · share it with your admin if it keeps happening` : "If it keeps happening, tell your admin what you were doing."}
    />
  );
}

export function NoAccess({ title, body, backTo = "/dashboard/my-day", backLabel = "Back to My Day" }: { title?: string; body?: string; backTo?: string; backLabel?: string }) {
  return (
    <StateCard
      icon={Lock}
      tone="neutral"
      title={title || "You don't have access to this"}
      body={body || "Your role doesn't include this page. If you need it, ask your sales head or administrator."}
      actions={
        <Link to={backTo} className={stateButton.outline}>
          {backLabel}
        </Link>
      }
    />
  );
}

export function NotFoundState({ title, body, backTo = "/dashboard/my-day", backLabel = "Go to My Day", children }: { title?: string; body?: string; backTo?: string; backLabel?: string; children?: React.ReactNode }) {
  return (
    <StateCard
      big="404"
      title={title || "That page doesn't exist"}
      body={body || "The link may be old, or the record was deleted or merged."}
      actions={
        <>
          {children}
          <Link to={backTo} className={stateButton.outline}>
            {backLabel}
          </Link>
        </>
      }
    />
  );
}

/**
 * "Ask for access": tells the person's own manager. The server answers the same whether or not the record
 * exists, so this can't be used to find out what is there.
 */
function AskForAccess({ type, id }: { type: string; id: string }) {
  const { success, error } = useToast();
  const [state, setState] = useState<"idle" | "busy" | "sent">("idle");
  const ask = async () => {
    setState("busy");
    try {
      const res = unwrapObject<{ sent_to: string | null }>(await api.post("/api/Notification/RequestAccess", { related_type: type, related_id: id }));
      setState("sent");
      success("Request sent", res.sent_to ? `${res.sent_to} has been asked.` : "Your administrator has been asked.");
    } catch (err: any) {
      setState("idle");
      error("Could not send the request", err.message || "Please try again");
    }
  };
  return (
    <button type="button" onClick={ask} disabled={state !== "idle"} className={`${stateButton.dark} disabled:opacity-70`}>
      {state === "sent" ? "Request sent" : state === "busy" ? "Sending…" : "Ask for access"}
    </button>
  );
}

/** What to show when a record page couldn't open it: not allowed, not there, or a hiccup */
export function RecordProblem({ error, what, backTo, backLabel, onRetry, record }: { error: unknown; what: string; backTo: string; backLabel: string; onRetry?: () => void; record?: { type: "DEAL" | "LEAD" | "ACCOUNT" | "CONTACT"; id?: string } }) {
  const status = error instanceof ApiError ? error.status : 0;
  const ask = record?.id ? <AskForAccess type={record.type} id={record.id} /> : undefined;
  if (status === 403) {
    return <NoAccess title={`You can't open this ${what}`} body={`It belongs to someone else's team. Sales executives see their own records; ask your sales head if you need it.`} backTo={backTo} backLabel={backLabel} />;
  }
  if (status === 404 || status === 400) {
    return (
      <NotFoundState title={`That ${what} isn't available to you`} body={`The link may be old, the ${what} may have been deleted or merged, or it may belong to someone else's team. If you should have it, ask for access.`} backTo={backTo} backLabel={backLabel}>
        {ask}
      </NotFoundState>
    );
  }
  return <ErrorState title={`Couldn't load this ${what}`} onRetry={onRetry} error={error} />;
}
